import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getDashboardStats, getPlants, deletePlant, bulkImport } from '../api';
import { categoryLabel } from '../data/categories';

export default function AdminDashboard({ token }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [plants, setPlants] = useState([]);
  const [showImport, setShowImport] = useState(false);
  const [csvData, setCsvData] = useState('');
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    getDashboardStats(token).then(setStats);
    getPlants().then(data => setPlants(data.plants || []));
  }, [token]);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This will also remove all photos.`)) return;
    await deletePlant(id, token);
    setPlants(plants.filter(p => p.plantId !== id));
  };

  const handleImport = async () => {
    if (!csvData.trim()) return;
    const result = await bulkImport(csvData, token);
    setImportResult(result);
    if (result.imported > 0) {
      getPlants().then(data => setPlants(data.plants || []));
      getDashboardStats(token).then(setStats);
    }
  };

  return (
    <>
      <div className="page-head page-head--row">
        <div>
          <p className="eyebrow">Admin</p>
          <h1 className="page-title">Dashboard</h1>
        </div>
        <div className="page-actions">
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowImport(!showImport)}>
            {showImport ? 'Hide import' : 'Bulk import'}
          </button>
          <Link to="/admin/plant/new" className="btn btn-primary btn-sm">Add plant</Link>
        </div>
      </div>

      {stats && (
        <div className="dashboard-stats">
          <div className="stat-card">
            <div className="stat-value">{stats.totalPlants}</div>
            <div className="stat-label">Total plants</div>
          </div>
          {Object.entries(stats.byCategory || {}).map(([cat, count]) => (
            <div className="stat-card" key={cat}>
              <div className="stat-value">{count}</div>
              <div className="stat-label">{categoryLabel(cat)}</div>
            </div>
          ))}
        </div>
      )}

      {showImport && (
        <div className="panel" style={{ marginBottom: 'var(--s-6)' }}>
          <h2 style={{ fontSize: 'var(--text-lg)', marginBottom: 'var(--s-2)' }}>Bulk import (CSV)</h2>
          <p className="auth-card__hint">
            Columns: plantName, scientificName, category, plantingDate, gardenLocation,
            careInstructions, growthNotes
          </p>
          <textarea
            className="code-input"
            value={csvData}
            onChange={e => setCsvData(e.target.value)}
            placeholder="plantName,scientificName,category,plantingDate,gardenLocation,careInstructions,growthNotes&#10;Tomato,Solanum lycopersicum,vegetables,2024-03-15,Bed A,Water daily,Growing well"
            aria-label="CSV data for bulk import"
          />
          <div className="page-actions" style={{ marginTop: 'var(--s-4)', alignItems: 'center' }}>
            <button type="button" className="btn btn-primary btn-sm" onClick={handleImport}>Import</button>
            {importResult && (
              <span className={importResult.errors?.length ? 'form-error' : 'auth-card__hint'} style={{ margin: 0 }}>
                Imported: {importResult.imported}
                {importResult.errors?.length ? ` | Errors: ${importResult.errors.length}` : ''}
              </span>
            )}
          </div>
        </div>
      )}

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Category</th>
              <th>Location</th>
              <th>Planted</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {plants.map(plant => (
              <tr key={plant.plantId}>
                <td>
                  <Link to={`/plant/${plant.plantId}`} className="admin-table__name">
                    {plant.plantName}
                  </Link>
                  {plant.scientificName && (
                    <div className="admin-table__sci">{plant.scientificName}</div>
                  )}
                </td>
                <td>{categoryLabel(plant.category)}</td>
                <td>{plant.gardenLocation || '-'}</td>
                <td>{plant.plantingDate || '-'}</td>
                <td>
                  <div className="admin-table__actions">
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate(`/admin/plant/${plant.plantId}/edit`)}>Edit</button>
                    <button type="button" className="btn btn-danger btn-sm" onClick={() => handleDelete(plant.plantId, plant.plantName)}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
            {plants.length === 0 && (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center', padding: 'var(--s-6)', color: 'var(--muted)' }}>
                  No plants yet. Add your first record to get started.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
